import { useMemo, useState, useCallback } from "react"
import { toast } from "react-toastify"
import { Col, Row, UncontrolledTooltip, Button, Badge } from "reactstrap"

// Common Components
import TableContainer from "components/Common/TableContainer"
import DeleteModal from "components/Common/DeleteModal"
import GlobalInputSearch from "components/Common/GlobalInputSearch"
import PageWrapper from "components/Common/PageWrapper"
import { CanView } from "components/Common/CanView"
import { ACCESS } from "constants/accessPermissions"

// CRUD Hooks
import {
  useAccessoryUseRequests,
  useDeleteAccessoryUseRequest,
} from "hooks/useAccessoryUseRequests"

// Utils
import { formatReadableDate } from "utils/formatDate"

// Local Components
import AccessoryUseRequestAddEdit from "./AccessoryUseRequestAddEdit"
import AccessoryUseRequestHeader from "./AccessoryUseRequestHeader"
import AccessoryUseRequestView from "./AccessoryUseRequestView"

const STATUS_BADGE = {
  pending: "warning",
  invoiced: "success",
  cancelled: "secondary",
}

const AccessoryUseRequestList = () => {
  document.title = "Accessory Use Requests"

  // -------------------- STATE --------------------
  const [modal, setModal] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [isView, setIsView] = useState(false)
  const [selectedData, setSelectedData] = useState(null)
  const [selectedViewData, setSelectedViewData] = useState(null)
  const [deleteModal, setDeleteModal] = useState(false)

  // -------------------- LIST PARAMS --------------------
  const [params, setParams] = useState({ page: 1, per_page: 10, search: "" })
  const setPage = page => setParams(prev => ({ ...prev, page }))
  const setSearch = search => setParams(prev => ({ ...prev, search, page: 1 }))

  // -------------------- API --------------------
  const {
    data: responseData,
    isLoading,
    refetch,
    isFetching,
  } = useAccessoryUseRequests(params)
  const { mutateAsync: deleteItem, isPending: deleteLoading } =
    useDeleteAccessoryUseRequest()

  // -------------------- MODAL HANDLERS --------------------
  const toggleModal = useCallback(() => {
    setModal(prev => !prev)
    if (modal) {
      setSelectedData(null)
      setIsEdit(false)
    }
  }, [modal])

  const handleAddNewClick = () => {
    setSelectedData(null)
    setIsEdit(false)
    setModal(true)
  }

  const handleViewClick = item => {
    setSelectedViewData(item)
    setIsView(true)
  }

  const handleEditClick = item => {
    setSelectedData(item)
    setIsEdit(true)
    setModal(true)
  }

  const handleDeleteClick = item => {
    setSelectedData(item)
    setDeleteModal(true)
  }

  const handleDelete = async () => {
    if (!selectedData?.id) return
    try {
      const res = await deleteItem(selectedData.id)
      toast.success(res?.message ?? "Request deleted successfully.")
      setDeleteModal(false)
      refetch()
    } catch (err) {
      console.error(err)
      toast.error(
        err?.response?.data?.message ?? "Failed to delete request."
      )
    }
  }

  // -------------------- TABLE COLUMNS --------------------
  const columns = useMemo(
    () => [
      {
        header: "Reference",
        accessorKey: "request_no",
        cell: ({ row }) => row.original.request_no || `#${row.original.id}`,
      },
      {
        header: "Vehicle",
        accessorKey: "vehicle",
        cell: ({ row }) => {
          const vehicle = row.original.vehicle
          if (!vehicle) return "-"
          return (
            <>
              <div className="fw-semibold">{vehicle.stock_id || "-"}</div>
              <div className="text-muted" style={{ fontSize: "0.85rem" }}>
                {vehicle.full_chassis}
              </div>
            </>
          )
        },
      },
      {
        header: "Requested By",
        accessorKey: "requested_by.name",
        cell: ({ row }) => row.original.requested_by?.name || "-",
      },
      {
        header: "Accessories",
        accessorKey: "items",
        cell: ({ row }) => (
          <Badge color="info">{row.original.items?.length || 0}</Badge>
        ),
      },
      {
        header: "Type",
        accessorKey: "is_self_vehicle",
        cell: ({ row }) => {
          const flag = row.original.is_self_vehicle
          if (flag === undefined || flag === null) return "-"
          return (
            <Badge color={flag === false ? "secondary" : "primary"}>
              {flag === false ? "Not self" : "Self"}
            </Badge>
          )
        },
      },
      {
        header: "Status",
        accessorKey: "status",
        cell: ({ row }) => {
          const status = row.original.status || "pending"
          return (
            <Badge color={STATUS_BADGE[status] || "secondary"} className="text-capitalize">
              {status}
            </Badge>
          )
        },
      },
      {
        header: "Date",
        accessorKey: "request_date",
        cell: ({ row }) =>
          row.original.request_date
            ? formatReadableDate(row.original.request_date)
            : "-",
      },
      {
        header: "Action",
        cell: ({ row }) => {
          const data = row.original
          const locked = (data.status || "pending") !== "pending"
          return (
            <div className="d-flex flex-wrap gap-1">
              <Button
                color="primary"
                size="sm"
                onClick={() => handleViewClick(data)}
                id={`view-${data.id}`}
              >
                <i className="mdi mdi-eye" />
              </Button>
              <UncontrolledTooltip target={`view-${data.id}`}>
                View
              </UncontrolledTooltip>

              <CanView access={[ACCESS.ACCESSORY_USE_REQUEST.EDIT]}>
                <Button
                  color="info"
                  size="sm"
                  onClick={() => handleEditClick(data)}
                  id={`edit-${data.id}`}
                  disabled={locked}
                >
                  <i className="mdi mdi-pencil-outline" />
                </Button>
                <UncontrolledTooltip target={`edit-${data.id}`}>
                  {locked ? "Linked to an invoice" : "Edit"}
                </UncontrolledTooltip>
              </CanView>

              <CanView access={[ACCESS.ACCESSORY_USE_REQUEST.DELETE]}>
                <Button
                  color="danger"
                  size="sm"
                  onClick={() => handleDeleteClick(data)}
                  id={`delete-${data.id}`}
                  disabled={locked}
                >
                  <i className="mdi mdi-delete-outline" />
                </Button>
                <UncontrolledTooltip target={`delete-${data.id}`}>
                  {locked ? "Linked to an invoice" : "Delete"}
                </UncontrolledTooltip>
              </CanView>
            </div>
          )
        },
      },
    ],
    []
  )

  const data = responseData?.data?.data || []
  const paginationInfo = responseData?.data

  // -------------------- RENDER --------------------
  return (
    <PageWrapper
      title="Accessory Use Requests"
      breadcrumbItem="Accessory Use Requests"
    >
      <AccessoryUseRequestHeader
        handleAddNewClick={handleAddNewClick}
        handleRefresh={refetch}
        resetParams={() => setParams({ page: 1, per_page: 10, search: "" })}
        isRefetching={isFetching}
        hasActiveFilters={!!params.search}
        total={paginationInfo?.total ?? 0}
      />

      <DeleteModal
        show={deleteModal}
        loading={deleteLoading}
        onDeleteClick={handleDelete}
        onCloseClick={() => setDeleteModal(false)}
      />

      <Row className="my-3">
        <Col sm={4}>
          <GlobalInputSearch value={params.search} onChange={setSearch} />
        </Col>
      </Row>

      <TableContainer
        columns={columns}
        data={data}
        isPagination
        paginationInfo={paginationInfo}
        onPageChange={setPage}
        loading={isLoading || isFetching}
        className="table-responsive table-hover"
      />

      <AccessoryUseRequestAddEdit
        modal={modal}
        isEdit={isEdit}
        editedData={selectedData}
        toggleModal={toggleModal}
        onSuccess={refetch}
      />

      <AccessoryUseRequestView
        isOpen={isView}
        data={selectedViewData}
        toggle={() => {
          setIsView(false)
          setSelectedViewData(null)
        }}
      />
    </PageWrapper>
  )
}

export default AccessoryUseRequestList
